import {
  Box,
  Stack,
  Typography,
  Divider,
  Avatar,
  LinearProgress,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  CircularProgress
} from '@mui/material';
import Grid from '@mui/material/Grid';
import MainCard from 'components/MainCard';
import BackToggle from 'components/third-party/dropzone/BackToggle';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { getAssessmentHistory, getPerformanceEvaluation, getBatchStudents } from 'services/evalution.service';

const getPcTypeLabel = (type: any): string => {
  if (!type) return '';
  if (typeof type === 'string') return type;
  if (Array.isArray(type)) {
    const titles = type.map((t) => (t && typeof t.title === 'string' ? t.title : '')).filter(Boolean);
    return titles.join(' / ');
  }
  if (typeof type === 'object' && typeof type.title === 'string') return type.title;
  return String(type);
};

const getPcMainType = (type: any): 'theory' | 'practical' | '' => {
  if (!type) return '';
  if (typeof type === 'string') {
    const t = type.toLowerCase();
    return t === 'theory' || t === 'practical' ? (t as 'theory' | 'practical') : '';
  }
  if (Array.isArray(type)) {
    const titles = type.map((t) => (t && typeof t.title === 'string' ? t.title.toLowerCase() : '')).filter(Boolean);
    if (titles.some((t) => t === 'task')) return 'practical';
    if (titles.some((t) => t === 'question' || t === 'viva')) return 'theory';
    return '';
  }
  return '';
};

const formatAssessmentHistoryDate = (value?: string) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

function buildStudentWithMarks(batchData: any, evalData: any, studentId: string) {
  const studentObj = (batchData?.students || []).find((s: any) => (s.user?._id || s.user?.id) === studentId);
  const name = studentObj?.user
    ? `${studentObj.user.firstName || ''} ${studentObj.user.lastName || ''}`.trim()
    : evalData?.studentId
      ? `${(evalData.studentId as any).firstName || ''} ${(evalData.studentId as any).lastName || ''}`.trim()
      : 'Student';
  const email = studentObj?.user?.email || (evalData?.studentId as any)?.email || '';

  const marksByPc: Record<string, any> = {};
  const rawEval: any[] = evalData?.evaluation || [];
  const isReassessment = String(batchData?.sessionType ?? '').toLowerCase() === 'reassessment';
  const evaluation: any[] = isReassessment ? rawEval.filter((n: any) => n.isReassessed === true) : rawEval;

  const nosList: any[] =
    evaluation.length > 0
      ? evaluation.map((nosEntry: any) => {
          const nosRef = nosEntry.nosId as any;
          return {
            nosId: nosRef?._id ?? nosRef,
            nosCode: nosRef?.nosCode ?? '',
            nosName: nosRef?.nosName ?? '',
            pcs: (nosEntry.pcs || []).map((pcEntry: any) => {
              const pcRef = pcEntry.pcId as any;
              return {
                pcId: pcRef?._id ?? pcRef,
                pcCode: pcRef?.pcCode ?? '',
                pcName: pcRef?.pcName ?? '',
                totalMarks: pcRef?.totalMarks ?? pcEntry.pcTotalMarks ?? 0,
                isCritical: pcRef?.isCritical ?? false,
                type: pcRef?.type ?? '',
                questions: (pcEntry.questions || []).map((q: any) => {
                  const qRef = q.questionId as any;
                  return {
                    questionId: qRef?._id ?? qRef,
                    question: qRef?.question ?? '',
                    maxMarks: 0,
                    obtainedMarks: q.obtainedMarks ?? 0
                  };
                })
              };
            })
          };
        })
      : isReassessment
        ? []
        : batchData?.nos || [];

  evaluation.forEach((nosEntry: any) => {
    (nosEntry.pcs || []).forEach((pcEntry: any) => {
      const pcId = (pcEntry.pcId?._id || pcEntry.pcId)?.toString?.();
      if (!pcId) return;

      let pcMeta: any = null;
      for (const nos of batchData?.nos || []) {
        pcMeta = (nos.pcs || []).find((p: any) => (p.pcId?._id ?? p.pcId)?.toString?.() === pcId);
        if (pcMeta) break;
      }

      const rawQuestions = pcMeta?.questions || [];
      const questions = rawQuestions.map((q: any) => {
        const qId = (q.questionId?._id ?? q.questionId ?? q.qId)?.toString?.();
        const found = (pcEntry.questions || []).find((eq: any) => (eq.questionId?._id || eq.questionId)?.toString?.() === qId);
        return {
          qId: q.questionId ?? q.qId,
          questionText: q.question ?? q.questionText ?? '',
          maxMarks: q.maxMarks ?? q.marks ?? '',
          marks: found != null ? String(found.obtainedMarks ?? '') : ''
        };
      });

      marksByPc[pcId] = {
        pcId,
        pcCode: pcMeta?.pcCode ?? (pcEntry.pcId as any)?.pcCode ?? '',
        pcName: pcMeta?.pcName ?? (pcEntry.pcId as any)?.pcName ?? '',
        maxMarks: pcMeta?.totalMarks ?? pcEntry.pcTotalMarks ?? 0,
        isCritical: Boolean(pcMeta?.isCritical),
        type: pcMeta?.type,
        questions
      };
    });
  });

  return { name, email, id: studentId, marksByPc, nosList };
}

const StudentResultDetail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const state =
    (location.state as {
      studentId?: string;
      batchId?: string;
      studentName?: string;
      assessmentSessionId?: string;
    }) || {};
  const { studentId, batchId, assessmentSessionId } = state;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [batchInfo, setBatchInfo] = useState<any>(null);
  const [student, setStudent] = useState<any>(null);
  const [assessmentHistory, setAssessmentHistory] = useState<any[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string | undefined>(assessmentSessionId);

  useEffect(() => {
    if (!batchId || !studentId) {
      setLoading(false);
      setError(true);
      return;
    }
    const fetchData = async () => {
      try {
        const [batchRes, evalRes, historyRes] = await Promise.all([
          getBatchStudents(batchId, currentSessionId || assessmentSessionId),
          getPerformanceEvaluation(batchId, studentId, currentSessionId || assessmentSessionId || ''),
          getAssessmentHistory(batchId, studentId)
        ]);
        const batchData = batchRes?.data?.data || batchRes?.data;
        const evalData = evalRes?.data?.data;
        const historyData = historyRes?.data?.data || [];
        if (!evalData) {
          setError(true);
          return;
        }
        const batchPayload = batchData && typeof batchData === 'object' ? batchData : { batchId, batchName: '', nos: [] };
        setBatchInfo(batchPayload);
        const built = buildStudentWithMarks(batchPayload, evalData, studentId);
        setStudent(built);
        setAssessmentHistory(Array.isArray(historyData) ? historyData : []);
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [batchId, studentId, assessmentSessionId, currentSessionId]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !student || !batchInfo) {
    return (
      <Box p={4}>
        <Typography color="error">Unable to load result. Please go back and try again.</Typography>
        <BackToggle show path="/result" />
      </Box>
    );
  }

  const initials = student.name
    ? student.name
        .split(' ')
        .map((n: string) => n[0])
        .slice(0, 2)
        .join('')
        .toUpperCase()
    : 'S';

  const qp = batchInfo?.qp || null;
  const nosList: any[] = student.nosList ?? batchInfo?.nos ?? [];

  const resolvePcKey = (pc: any) => (pc?.pcId?._id ?? pc?.pcId)?.toString?.() ?? String(pc?.pcId ?? '');

  const grandTotal =
    nosList.length > 0
      ? nosList.reduce((sum: number, nos: any) => {
          const pcSum = (nos.pcs || []).reduce((s: number, pc: any) => {
            const pcKey = resolvePcKey(pc);
            const pcData = student.marksByPc?.[pcKey] || {};
            return s + (pcData.questions || []).reduce((qSum: number, q: any) => qSum + (Number(q.marks) || 0), 0);
          }, 0);
          return sum + pcSum;
        }, 0)
      : Object.values(student.marksByPc).reduce(
          (sum: number, pc: any) => sum + (pc.questions || []).reduce((s: number, q: any) => s + (Number(q.marks) || 0), 0),
          0
        );

  const grandMax =
    nosList.length > 0
      ? nosList.reduce((sum: number, nos: any) => {
          const pcMax = (nos.pcs || []).reduce((s: number, pc: any) => {
            const pcKey = resolvePcKey(pc);
            const pcData = student.marksByPc?.[pcKey] || {};
            return s + (Number(pc.totalMarks ?? pcData.maxMarks ?? 0) || 0);
          }, 0);
          return sum + pcMax;
        }, 0)
      : Object.values(student.marksByPc).reduce((sum: number, pc: any) => sum + (Number(pc.maxMarks) || 0), 0);

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <MainCard
        title={
          <Stack direction="row" alignItems="center" spacing={2}>
            <BackToggle show path="/result" />
            <Box>
              <Typography variant="h5">Result Details</Typography>
              <Typography variant="caption" color="text.secondary">
                {batchInfo?.batchName || ''}
              </Typography>
            </Box>
          </Stack>
        }
        content={false}
        secondary={
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ pr: 2 }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40, fontSize: 15, fontWeight: 700 }}>{initials}</Avatar>
            <Box>
              <Typography variant="subtitle2" fontWeight={700}>
                {student.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {student.email}
              </Typography>
            </Box>
          </Stack>
        }
      />
      <MainCard content={false}>
        <Box sx={{ px: 3, py: 2 }}>
          <Grid container spacing={2} alignItems="flex-start">
            {batchInfo?.courseName && (
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
                  Course
                </Typography>
                <Box mt={0.25}>
                  <Chip label={batchInfo.courseName} size="small" color="secondary" />
                </Box>
              </Grid>
            )}
            {batchInfo?.templateName && (
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
                  Template
                </Typography>
                <Box mt={0.25}>
                  <Chip label={batchInfo.templateName} size="small" color="secondary" />
                </Box>
              </Grid>
            )}
            {qp && (
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
                  Qualification Pack
                </Typography>
                <Stack direction="row" alignItems="center" spacing={0.75} mt={0.25} flexWrap="wrap" useFlexGap>
                  <Chip label={qp.qpCode} size="small" color="secondary" />
                  <Typography variant="body2" fontWeight={600}>
                    {qp.qpName}
                  </Typography>
                </Stack>
              </Grid>
            )}
          </Grid>
        </Box>
      </MainCard>
      <MainCard content={false}>
        <Box sx={{ px: 3, py: 2 }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
            Assessment History
          </Typography>
          {assessmentHistory.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {assessmentHistory.map((history) => {
                const attemptNumber = history?.assessmentSessionId?.attemptNumber ?? '-';
                const percentage = history?.percentage;
                const result = history?.result || '-';
                const obtainedMarks = history?.obtainedMarks ?? '-';
                const createdAt = formatAssessmentHistoryDate(history?.created_at);
                const sessionId = history?.assessmentSessionId?._id || history?.assessmentSessionId;
                const isActive = currentSessionId
                  ? String(currentSessionId) === String(sessionId)
                  : String(assessmentSessionId || '') === String(sessionId);

                return (
                  <Box
                    key={history?._id || `${attemptNumber}-${createdAt}`}
                    sx={{
                      p: 2,
                      border: '1px solid',
                      borderColor: isActive ? 'primary.main' : 'divider',
                      borderRadius: 2,
                      bgcolor: isActive ? 'primary.lighter' : 'background.paper'
                    }}
                  >
                    <Grid container spacing={2} alignItems="center">
                      <Grid size={{ xs: 12, sm: 6, md: 2.5 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
                          Attempt Number
                        </Typography>
                        <Typography variant="body2" fontWeight={700}>
                          {attemptNumber}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 2.5 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
                          Obtained Marks
                        </Typography>
                        <Typography variant="body2" fontWeight={700}>
                          {obtainedMarks}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 2.5 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
                          Percentage
                        </Typography>
                        <Typography variant="body2" fontWeight={700}>
                          {percentage != null ? `${percentage}%` : '-'}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 2.5 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
                          Result
                        </Typography>
                        <Box>
                          <Chip
                            label={result}
                            size="small"
                            color={result === 'Pass' ? 'success' : result === 'Fail' ? 'error' : 'default'}
                            variant={result === 'Pass' || result === 'Fail' ? 'filled' : 'outlined'}
                            sx={{ fontWeight: 700, mt: 0.25 }}
                          />
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 2.5 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
                          Created At
                        </Typography>
                        <Typography variant="body2" fontWeight={700}>
                          {createdAt}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                        <Button
                          variant={isActive ? 'contained' : 'outlined'}
                          size="small"
                          onClick={() => {
                            if (!sessionId || isActive) return;
                            setCurrentSessionId(String(sessionId));
                            setLoading(true);
                          }}
                        >
                          {isActive ? 'Viewing this attempt' : 'View this attempt'}
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No assessment history available.
            </Typography>
          )}
        </Box>
      </MainCard>
      {nosList.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0, borderRadius: 2, overflow: 'hidden', boxShadow: 1 }}>
          {nosList.map((nos: any, nosIdx: number) => {
            const nosTotal = (nos.pcs || []).reduce((sum: number, pc: any) => {
              const pcKey = resolvePcKey(pc);
              const pcData = student.marksByPc?.[pcKey] || {};
              return sum + (pcData.questions || []).reduce((s: number, q: any) => s + (Number(q.marks) || 0), 0);
            }, 0);
            const nosMax = (nos.pcs || []).reduce((sum: number, pc: any) => {
              const pcKey = resolvePcKey(pc);
              const pcData = student.marksByPc?.[pcKey] || {};
              return sum + (Number(pc.totalMarks ?? pcData.maxMarks ?? 0) || 0);
            }, 0);
            const nosPercent = nosMax > 0 ? Math.round((nosTotal / nosMax) * 100) : 0;

            return (
              <Accordion
                key={(nos.nosId?._id ?? nos.nosId)?.toString?.() ?? nosIdx}
                defaultExpanded={nosIdx === 0}
                sx={{
                  '&:before': { display: 'none' },
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '0 !important',
                  '&:first-of-type': { borderTopLeftRadius: '8px !important', borderTopRightRadius: '8px !important' },
                  '&:last-of-type': { borderBottomLeftRadius: '8px !important', borderBottomRightRadius: '8px !important' },
                  '& + .MuiAccordion-root': { borderTop: 0 }
                }}
              >
                <AccordionSummary
                  sx={{
                    bgcolor: 'primary.lighter',
                    px: 2,
                    minHeight: 56,
                    '&.Mui-expanded': { minHeight: 56 }
                  }}
                >
                  <Box sx={{ width: '100%', pr: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                        <Typography variant="h6" fontWeight={700}>
                          NOS:{' '}
                        </Typography>
                        <Chip label={nos.nosCode} size="small" color="primary" sx={{ fontWeight: 700 }} />
                        <Typography variant="h6" fontWeight={700} color="primary.main">
                          {nos.nosName}
                        </Typography>
                      </Stack>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                        Total Marks: {nosMax}
                      </Typography>
                    </Box>
                    <Box sx={{ width: 180, textAlign: 'right', flexShrink: 0 }}>
                      <Typography variant="subtitle2" fontWeight={700} color="primary.main">
                        {nosTotal} / {nosMax}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(nosPercent, 100)}
                        color={nosPercent >= 80 ? 'success' : nosPercent >= 50 ? 'warning' : 'primary'}
                        sx={{ height: 6, borderRadius: 1, mt: 0.5 }}
                      />
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0, bgcolor: 'background.paper' }}>
                  <Divider />
                  <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {(nos.pcs || []).map((pc: any, pcIdx: number) => {
                      const pcKey = resolvePcKey(pc);
                      const pcIsCritical = Boolean(pc.isCritical);
                      const pcData = student.marksByPc?.[pcKey] || {};
                      const questions = pcData.questions || [];
                      const pcTotal = questions.reduce((sum: number, q: any) => sum + (Number(q.marks) || 0), 0);
                      const pcMax = pc.totalMarks ?? pcData.maxMarks ?? 0;
                      const pcPercent = pcMax > 0 ? Math.round((pcTotal / pcMax) * 100) : 0;
                      const pcTypeLabel = getPcTypeLabel(pc.type) || '-';

                      return (
                        <Accordion
                          key={pcKey ?? pcIdx}
                          defaultExpanded={pcIdx === 0}
                          sx={{
                            '&:before': { display: 'none' },
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: '8px !important',
                            overflow: 'hidden',
                            '& .MuiAccordionSummary-root': { bgcolor: 'grey.50' },
                            '& .MuiAccordionDetails-root': { p: 1.5 }
                          }}
                        >
                          <AccordionSummary sx={{ minHeight: 48, '&.Mui-expanded': { minHeight: 48 } }}>
                            <Box sx={{ width: '100%', pr: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                                <Typography variant="subtitle2" fontWeight={700}>
                                  {pc.pcCode} — {pc.pcName}
                                </Typography>
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  sx={{ height: 20, fontWeight: 500, fontSize: '0.78rem' }}
                                  label={
                                    <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Typography
                                        component="span"
                                        sx={{ textTransform: 'capitalize', fontWeight: 500, fontSize: '0.78rem' }}
                                      >
                                        {pcTypeLabel}
                                      </Typography>
                                      <Typography
                                        component="span"
                                        sx={{
                                          color: pcIsCritical ? 'error.main' : 'text.secondary',
                                          fontWeight: 500,
                                          ml: 0.5,
                                          textTransform: 'none',
                                          fontSize: '0.78rem'
                                        }}
                                      >
                                        - {pcIsCritical ? 'Critical' : 'Non-critical'}
                                      </Typography>
                                    </Box>
                                  }
                                />
                              </Stack>
                              <Box sx={{ width: 160, textAlign: 'right', flexShrink: 0 }}>
                                <Typography variant="caption" fontWeight={700}>
                                  {pcTotal} / {pcMax}
                                </Typography>
                                <LinearProgress
                                  variant="determinate"
                                  value={Math.min(pcPercent, 100)}
                                  color={pcPercent >= 80 ? 'success' : pcPercent >= 50 ? 'warning' : 'primary'}
                                  sx={{ height: 5, borderRadius: 1, mt: 0.25 }}
                                />
                              </Box>
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              {questions.length > 0 ? (
                                questions.map((q: any, qIdx: number) => (
                                  <Box
                                    key={q.qId || qIdx}
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 2,
                                      p: 1.25,
                                      borderRadius: 1,
                                      bgcolor: 'background.paper',
                                      border: '1px solid',
                                      borderColor: 'divider'
                                    }}
                                  >
                                    <Chip label={`Q${qIdx + 1}`} size="small" color="secondary" sx={{ fontWeight: 700, minWidth: 36 }} />
                                    <Box sx={{ flex: 1 }}>
                                      <Typography variant="body2" fontWeight={600}>
                                        {q.questionText || q.question || `Question ${qIdx + 1}`}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        Max: {q.maxMarks} mark{q.maxMarks > 1 ? 's' : ''}
                                      </Typography>
                                    </Box>
                                    <Typography variant="body2" fontWeight={700} color="primary.main">
                                      {q.marks ?? '—'}
                                    </Typography>
                                  </Box>
                                ))
                              ) : (
                                <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                                  No questions available for this PC.
                                </Typography>
                              )}
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                  </Box>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      ) : (
        <Box display="flex" flexDirection="column" gap={2}>
          {Object.entries(student.marksByPc).map(([pcId, pc]: [string, any]) => {
            const total = pc.questions.reduce((sum: number, q: any) => sum + (Number(q.marks) || 0), 0);
            const max = pc.maxMarks ?? 0;
            const percent = max > 0 ? Math.round((total / max) * 100) : 0;
            return (
              <MainCard key={pcId} content={false}>
                <Box sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6" fontWeight={700}>
                      {pc.pcCode ? `${pc.pcCode} — ${pc.pcName}` : pc.pcName}
                    </Typography>
                    <Typography variant="subtitle2" fontWeight={700}>
                      {total} / {max}
                    </Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={Math.min(percent, 100)} sx={{ height: 6, borderRadius: 1, mb: 1.5 }} />
                  <Divider sx={{ mb: 1.5 }} />
                  {pc.questions.map((q: any, i: number) => (
                    <Box key={q.qId || i} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                      <Chip label={`Q${i + 1}`} size="small" color="secondary" />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {q.questionText}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Max: {q.maxMarks}
                        </Typography>
                      </Box>
                      <Typography variant="body2" fontWeight={700}>
                        {q.marks ?? '—'}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </MainCard>
            );
          })}
        </Box>
      )}
      <MainCard content={false}>
        <Box sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Grand Total
            </Typography>
            <Typography variant="h5" fontWeight={700}>
              {grandTotal} / {grandMax}
            </Typography>
          </Box>
          <Button variant="outlined" onClick={() => navigate('/result')}>
            Back
          </Button>
        </Box>
      </MainCard>
    </Box>
  );
};

export default StudentResultDetail;
